import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, History, Plane, X } from "lucide-react";
import API from "../services/api";
import BottomNav from "../components/BottomNav";

function AviatorPage() {
  const navigate = useNavigate();
  const user = useMemo(() => JSON.parse(localStorage.getItem("user") || "null"), []);

  const [loading, setLoading]   = useState(false);
  const [lobbyURL, setLobbyURL] = useState(null);
  const [error, setError]       = useState("");

  const launchGame = async () => {
    if (!user?.id) { navigate("/login"); return; }
    setLoading(true);
    setError("");
    try {
      const res = await API.post("/api/aviator/launch", { user_id: user.id });
      setLobbyURL(res.data.lobbyURL);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to launch game. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const closeGame = () => {
    setLobbyURL(null);
    navigate("/dashboard");
  };

  // ── Fullscreen iframe while game is open ───────────────────────────────────
  if (lobbyURL) {
    return (
      <div style={iframeWrap}>
        <div style={iframeBar}>
          <span style={iframeTitle}>✈ AVIATOR</span>
          <button type="button" style={iframeClose} onClick={closeGame}>
            <X size={20} color="#fff" />
          </button>
        </div>
        <iframe
          src={lobbyURL}
          title="Aviator Game"
          style={iframeEl}
          allow="autoplay; fullscreen; payment; clipboard-write; microphone; camera"
          allowFullScreen
        />
      </div>
    );
  }

  // ── Launch screen ──────────────────────────────────────────────────────────
  return (
    <div style={page}>
      <header style={hdr}>
        <button type="button" style={backBtn} onClick={() => navigate("/dashboard")}>
          <ChevronLeft size={22} color="#fff" />
        </button>
        <span style={hdrTitle}>✈ AVIATOR</span>
        <button type="button" style={histBtn} onClick={() => navigate("/casino-bets")} title="Bet History">
          <History size={20} color="#facc15" />
        </button>
      </header>

      <main style={mainWrap}>

        {/* Hero card */}
        <div style={heroCard}>
          <div style={heroGlow} />
          <div style={heroContent}>
            <div style={planeBig}>✈</div>
            <h1 style={heroH1}>AVIATOR</h1>
            <p style={heroSub}>The higher it flies, the more you win.<br />Cash out before it crashes!</p>

            <div style={featureRow}>
              {["Live Multiplier", "Instant Cashout", "Real Money"].map((f) => (
                <div key={f} style={featureChip}>{f}</div>
              ))}
            </div>

            {!!error && <div style={errorBox}>{error}</div>}

            <button
              type="button"
              style={loading ? launchBtnDisabled : launchBtn}
              onClick={launchGame}
              disabled={loading}
            >
              {loading ? (
                <span style={loadDot}>Launching...</span>
              ) : (
                <>
                  <Plane size={20} />
                  PLAY AVIATOR NOW
                </>
              )}
            </button>

            <p style={disclaimer}>
              18+ Only. This game involves real money. Please play responsibly.
            </p>
          </div>
        </div>

        {/* How to play */}
        <div style={howCard}>
          <h3 style={howTitle}>How to Play</h3>
          <div style={howSteps}>
            {[
              { n: "1", t: "Place Your Bet", d: "Enter your bet amount before the plane takes off." },
              { n: "2", t: "Watch It Fly",  d: "The multiplier grows as the plane climbs higher." },
              { n: "3", t: "Cash Out",      d: "Hit Cash Out before the plane crashes to win!" },
            ].map((s) => (
              <div key={s.n} style={howStep}>
                <div style={howNum}>{s.n}</div>
                <div>
                  <div style={howStepTitle}>{s.t}</div>
                  <div style={howStepDesc}>{s.d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

      </main>

      <BottomNav />
    </div>
  );
}

/* ── styles ── */
const DARK   = "#0a0a1a";
const CARD   = "#13132a";
const BORDER = "rgba(255,255,255,0.08)";
const YELLOW = "#facc15";
const PURPLE = "#7c3aed";

const page   = { minHeight: "100vh", background: DARK, fontFamily: "Inter, system-ui, sans-serif", color: "#fff", paddingBottom: 90 };

const hdr    = { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 14px", height: 54, background: "#0d0d22", borderBottom: `1px solid ${BORDER}`, position: "sticky", top: 0, zIndex: 50 };
const backBtn= { border: 0, background: "transparent", cursor: "pointer", padding: 4, display: "grid", placeItems: "center" };
const hdrTitle= { fontSize: 18, fontWeight: 900, letterSpacing: "0.1em", color: "#fff" };
const histBtn = { border: 0, background: "transparent", cursor: "pointer", padding: 6, display: "grid", placeItems: "center" };

const mainWrap= { padding: "16px 14px", maxWidth: 480, margin: "0 auto", display: "grid", gap: 14 };

const heroCard   = { borderRadius: 20, border: `1px solid rgba(124,58,237,0.4)`, background: `linear-gradient(160deg, #0d0d22 0%, #1e1b4b 70%, #312e81 100%)`, overflow: "hidden", position: "relative" };
const heroGlow   = { position: "absolute", top: -80, right: -80, width: 260, height: 260, borderRadius: "50%", background: "radial-gradient(circle, rgba(124,58,237,0.25) 0%, transparent 70%)", pointerEvents: "none" };
const heroContent= { padding: "32px 24px 28px", display: "flex", flexDirection: "column", alignItems: "center", gap: 14, textAlign: "center", position: "relative" };
const planeBig   = { fontSize: 64, lineHeight: 1, filter: "drop-shadow(0 0 20px rgba(250,204,21,0.5))" };
const heroH1     = { margin: 0, fontSize: 36, fontWeight: 900, color: YELLOW, letterSpacing: "0.12em" };
const heroSub    = { margin: 0, fontSize: 14, color: "rgba(255,255,255,0.7)", lineHeight: 1.6 };
const featureRow = { display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "center" };
const featureChip= { fontSize: 11, fontWeight: 800, padding: "5px 12px", borderRadius: 999, background: "rgba(255,255,255,0.1)", color: "#e2e8f0", border: `1px solid ${BORDER}` };
const errorBox   = { width: "100%", padding: "10px 14px", borderRadius: 12, background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.3)", color: "#fca5a5", fontSize: 13, fontWeight: 700 };
const launchBtn  = { display: "flex", alignItems: "center", justifyContent: "center", gap: 10, width: "100%", height: 56, border: 0, borderRadius: 16, background: `linear-gradient(135deg, ${PURPLE}, #4f46e5)`, color: "#fff", fontSize: 17, fontWeight: 900, cursor: "pointer", letterSpacing: "0.05em", boxShadow: `0 8px 28px rgba(124,58,237,0.5)`, marginTop: 4 };
const launchBtnDisabled = { ...launchBtn, background: "#1e1e3a", color: "#475569", boxShadow: "none", cursor: "not-allowed" };
const loadDot    = { fontSize: 16, color: "#94a3b8" };
const disclaimer = { margin: 0, fontSize: 11, color: "rgba(255,255,255,0.35)", lineHeight: 1.5 };

const howCard    = { background: CARD, border: `1px solid ${BORDER}`, borderRadius: 16, padding: 18 };
const howTitle   = { margin: "0 0 14px", fontSize: 15, fontWeight: 900, color: YELLOW };
const howSteps   = { display: "grid", gap: 14 };
const howStep    = { display: "flex", gap: 14, alignItems: "flex-start" };
const howNum     = { width: 30, height: 30, borderRadius: "50%", background: PURPLE, color: "#fff", fontWeight: 900, fontSize: 14, display: "grid", placeItems: "center", flexShrink: 0 };
const howStepTitle= { fontSize: 13, fontWeight: 800, color: "#e2e8f0", marginBottom: 3 };
const howStepDesc = { fontSize: 12, color: "#94a3b8", lineHeight: 1.5 };

// iframe styles
const iframeWrap = { position: "fixed", inset: 0, zIndex: 9999, display: "flex", flexDirection: "column", background: "#000", touchAction: "none" };
const iframeBar  = { height: 46, background: "#0d0d22", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 14px", flexShrink: 0, borderBottom: "1px solid rgba(255,255,255,0.08)", touchAction: "auto" };
const iframeTitle= { fontSize: 15, fontWeight: 900, color: "#facc15", letterSpacing: "0.08em" };
const iframeClose= { border: 0, background: "transparent", cursor: "pointer", display: "grid", placeItems: "center", padding: 6 };
const iframeEl   = { flex: 1, border: "none", width: "100%", minHeight: 0, touchAction: "auto", WebkitOverflowScrolling: "touch", display: "block" };

export default AviatorPage;
